/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { BaseCrudService, API_CONFIG, HttpService } from '@nebula-shared/data-access';

export interface CentroCostoDto {
  id: number;
  codigo: string;
  nombre: string;
  idTipoCentroCostos?: number;
  nombreTipoCentroCostos?: string;
  status: string;
  esPadre: boolean;
  movimiento: boolean;
  clientId?: number;
  companyId?: number;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
  updatedBy?: string;
  version?: number;
}

export interface PageResponseCentroCosto {
  content: CentroCostoDto[];
  size: number;
  number: number;
  numberOfElements: number;
  totalElements: number;
  totalPages: number;
  empty: boolean;
}

@Injectable({ providedIn: 'root' })
export class CostCenterService extends BaseCrudService<any, any, any> {
  readonly httpService = inject(HttpService);

  constructor() {
    super();
    const config = API_CONFIG.accounting;
    this.setBaseUrl(`${config.baseUrl}${config.endpoints.centroCostos || '/centros-costos'}`);
  }

  /**
   * Obtener centros de costo por tipo (solo activos)
   */
  getCentrosByTipo(tipoId: number): Observable<CentroCostoDto[]> {
    const body = {
      searchsBy: [
        {
          key: 'tipoCentroCostoId',
          operation: 'EQUAL',
          value: tipoId,
        },
        {
          key: 'status',
          operation: 'EQUAL',
          value: 'ACTIVE',
        },
      ],
      page: 0,
      size: 1000,
      ordersBy: [{ column: 'codigo', direction: 'ASC' }],
    };

    return this.httpService.post<any>(`${this.baseUrl}/page-response`, body).pipe(
      map((response: any) => {
        const content = this.extractContent(response);
        return content.map((item: any) => this.mapToDto(item));
      }),
    );
  }

  /**
   * Obtener todos los centros de costo activos
   */
  getActiveCentros(): Observable<CentroCostoDto[]> {
    const body = {
      searchsBy: [
        {
          key: 'status',
          operation: 'EQUAL',
          value: 'ACTIVE',
        },
      ],
      page: 0,
      size: 1000,
      ordersBy: [{ column: 'codigo', direction: 'ASC' }],
    };

    return this.httpService.post<any>(`${this.baseUrl}/page-response`, body).pipe(
      map((response: any) => {
        const content = this.extractContent(response);
        return content.map((item: any) => this.mapToDto(item));
      }),
    );
  }

  /**
   * Buscar centros de costo por término de búsqueda (código o nombre)
   * Ahora filtra directamente en el backend
   */
  searchCentros(searchTerm: string): Observable<CentroCostoDto[]> {
    const body = {
      searchsBy: [
        {
          key: 'status',
          operation: 'EQUAL',
          value: 'ACTIVE',
        },
        {
          key: 'codigo',
          operation: 'MATCH',
          value: searchTerm,
        },
      ],
      page: 0,
      size: 1000,
      ordersBy: [{ column: 'codigo', direction: 'ASC' }],
    };

    return this.httpService.post<any>(`${this.baseUrl}/page-response`, body).pipe(
      map((response: any) => {
        const content = this.extractContent(response);
        return content.map((item: any) => this.mapToDto(item));
      }),
    );
  }

  /**
   * Buscar un centro de costo específico por código (solo activos)
   */
  getCentroByCodigo(codigo: string): Observable<CentroCostoDto | null> {
    const body = {
      searchsBy: [
        {
          key: 'status',
          operation: 'EQUAL',
          value: 'ACTIVE',
        },
        {
          key: 'codigo',
          operation: 'EQUAL',
          value: codigo,
        },
      ],
      page: 0,
      size: 10,
      ordersBy: [{ column: 'codigo', direction: 'ASC' }],
    };

    return this.httpService.post<any>(`${this.baseUrl}/page-response`, body).pipe(
      map((response: any) => {
        const content = this.extractContent(response);
        return content.length > 0 ? this.mapToDto(content[0]) : null;
      }),
    );
  }

  /**
   * Buscar un centro de costo específico por ID (solo activos)
   */
  getCentroById(id: number): Observable<CentroCostoDto | null> {
    const body = {
      searchsBy: [
        {
          key: 'status',
          operation: 'EQUAL',
          value: 'ACTIVE',
        },
        {
          key: 'id',
          operation: 'EQUAL',
          value: id,
        },
      ],
      page: 0,
      size: 10,
      ordersBy: [{ column: 'codigo', direction: 'ASC' }],
    };

    return this.httpService.post<any>(`${this.baseUrl}/page-response`, body).pipe(
      map((response: any) => {
        const content = this.extractContent(response);
        return content.length > 0 ? this.mapToDto(content[0]) : null;
      }),
    );
  }

  /**
   * Extraer contenido de la respuesta
   * La estructura es: response.content.content (donde el primer content es el wrapper y el segundo es el array)
   */
  private extractContent(response: any): any[] {
    return (
      response?.response?.content?.content || response?.content?.content || response?.content || []
    );
  }

  /**
   * Mapear a DTO
   */
  private mapToDto(item: any): CentroCostoDto {
    let status = item.status || item.estado;
    if (status === 'accounting.centro_costos.estado.activo') status = 'ACTIVE';
    if (status === 'accounting.centro_costos.estado.inactivo') status = 'INACTIVE';
    if (status === 'accounting.centro_costos.estado.bloqueado') status = 'BLOCKED';

    return {
      id: item.id,
      codigo: item.codigo,
      nombre: item.nombre,
      idTipoCentroCostos: item.tipoCentroCostoId || item.idTipoCentroCostos,
      nombreTipoCentroCostos: item.tipoCentroCostoNombre || item.nombreTipoCentroCostos,
      status: status,
      esPadre: item.esPadre === true || item.esPadre === 'true' || false,
      movimiento: item.movimiento === true || item.movimiento === 'true' || false,
      clientId: item.clientId,
      companyId: item.companyId,
      createdAt: item.createdAt,
      updatedAt: item.updatedAt,
      createdBy: item.createdBy,
      updatedBy: item.updatedBy,
      version: item.version,
    };
  }
}
